import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import { StatusBanner } from "@/components/common/StatusBanner";
import { useVideo } from "../data/useVideo";
import { useSignedPlaybackUrl } from "../data/useSignedPlaybackUrl";
import { VideoPlayer } from "../components/VideoPlayer";
import { Button } from "@/components/ui/button";
import { RecentRunsList } from "../components/RecentRunsList";
import { useRef, useState } from "react";

export function SwingLabAnalysisPage() {
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get('id');
  
  const { data: video } = useVideo(videoId || undefined);
  const { url: signedUrl, isLoading: urlLoading, error: urlError } = useSignedPlaybackUrl(
    video?.playbackPolicy === 'public' ? undefined : video?.muxPlaybackId || undefined
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleRunAnalysis = async () => {
    if (!video?.muxPlaybackId) return;
    
    setIsRunning(true);
    // Placeholder for actual analysis
    setTimeout(() => {
      setIsRunning(false);
      console.log('Analysis would run here');
    }, 2000);
  };

  if (!videoId) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="max-w-4xl mx-auto px-5 py-8">
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">
            Swing Lab Analysis
          </h1>
          <p className="text-slate-600 mb-8">
            Upload a video to begin analysis.
          </p>
          <Link
            to="/swing-lab/upload"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90"
          >
            Upload Video
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="mb-6">
          <Link 
            to="/swing-lab/upload"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Upload
          </Link>
          <h1 className="text-3xl font-semibold text-slate-900 mb-2">
            Swing Lab Analysis
          </h1>
        </div>

        <StatusBanner
          type="info"
          title="Swing Lab v3 - Persistence Ready"
          description="Analysis results will be saved and can be reloaded without reprocessing."
          className="mb-8"
        />

        {isRunning && (
          <StatusBanner
            type="info"
            title="Running Analysis..."
            description="Processing video and saving results to storage."
            className="mb-8"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <VideoPlayer
              signedUrl={signedUrl}
              isLoading={urlLoading}
              error={urlError}
              onCopyUrl={() => {}}
              videoRef={videoRef}
              onPlayStateChange={() => {}}
              video={video}
              onNativeVideo={() => {}}
            />
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Analysis</h3>
              <Button
                onClick={handleRunAnalysis}
                disabled={isRunning || !signedUrl}
                className="w-full"
              >
                <Play className="h-4 w-4 mr-2" />
                {isRunning ? 'Running...' : 'Run Analysis'}
              </Button>
            </div>

            {video?.muxPlaybackId && (
              <RecentRunsList 
                videoAssetId={video.muxPlaybackId}
                onReloadRun={(run) => console.log('Reload run:', run)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}